/// <reference path="../../types.d.ts" />

import { HttpMethods } from '@codametrix/ui-common';
import { api } from '../../core/net';
import { ArgusFilters } from './filters';

const ANALYTICS_BASE = '/api/bff/analytics/v1';

export const AUTOMATION_DATASET = 'automation_case';

export interface FetchSummaryParams extends ArgusFilters {
  dos_start: string;
  dos_end: string;
}

export interface AutomationSummaryResponse {
  dataAsOf: string;
  kpiRollup: {
    totalCases: number;
    eligibleCases: number;
    automatedCases: number;
    automationRate: number;
    automationRateNumerator: number;
    automationRateDenominator: number;
  };
  dailyTrend: Array<{
    snapshotDate: string;
    totalCases: number;
    automatedCases: number;
    automationRate: number;
  }>;
  volumeByCaseType: Array<{ caseType: string; caseCount: number }>;
  resultsBreakdown: {
    buckets: Array<{ subcategory: string; caseCount: number }>;
    uncategorizedCount: number;
  };
}

export interface TenantOption {
  code: string;
  name: string;
}

export interface FilterOptionsResponse {
  dataAsOf: string;
  healthSystems: string[];
  serviceLines: string[];
  subServiceLines: string[];
  categories: string[];
  /** Tenants (sites) the caller may filter on; empty when the data has no tenant dimension. */
  tenants: TenantOption[];
}

export interface DatasetFreshnessResponse {
  dataset: string;
  dataAsOf: string;
  status: string;
  stale: boolean;
  stalenessThresholdMinutes: number;
}

const buildSearchParams = (params: FetchSummaryParams): string => {
  const search = new URLSearchParams();
  search.set('startDate', params.dos_start);
  search.set('endDate', params.dos_end);
  search.set('eligibleOnly', 'true');
  if (params.health_system_code) {
    search.set('healthSystemCode', params.health_system_code);
  }
  if (params.service_line) {
    search.set('serviceLine', params.service_line);
  }
  if (params.tenant_code) {
    search.set('tenantCode', params.tenant_code);
  }
  return search.toString();
};

export const buildSummaryEndpoint = (params: FetchSummaryParams): string =>
  `${ANALYTICS_BASE}/automation/summary?${buildSearchParams(params)}`;

export const buildFilterOptionsEndpoint = (): string =>
  `${ANALYTICS_BASE}/automation/filter-options`;

export const buildFreshnessEndpoint = (
  dataset: string = AUTOMATION_DATASET
): string => `${ANALYTICS_BASE}/datasets/${dataset}/freshness`;

/**
 * The service reports rates as a 0-1 fraction (`0.834`); the view model, its
 * `formatPct` and the trend chart's right axis all work in percent (`83.4`).
 */
const toPercent = (fraction: number): number => fraction * 100;

/**
 * Maps the analytics service response onto the view's summary shape.
 *
 * The service supplies neither period-over-period comparisons nor an
 * automated/manual split per trend point or per case type, so `previous`,
 * the four-way trend breakdown and the per-category split are left absent
 * rather than synthesised. `resultsBreakdown` carries seven subcategories
 * where the view originally assumed four; all seven are passed through,
 * keyed by the service's enum names lowercased.
 */
export const adaptSummary = (
  response: AutomationSummaryResponse,
  params: FetchSummaryParams
): CMx.AutomationDashboardSummary => {
  const { kpiRollup } = response;
  const manualCases = kpiRollup.totalCases - kpiRollup.automatedCases;

  const automationResults: { [subcategory: string]: number } = {};
  response.resultsBreakdown.buckets.forEach(bucket => {
    automationResults[bucket.subcategory.toLowerCase()] = bucket.caseCount;
  });
  automationResults.uncategorized = response.resultsBreakdown.uncategorizedCount;

  return {
    data: {
      dataAsOf: response.dataAsOf,
      kpis: {
        current: {
          total_cases: kpiRollup.totalCases,
          automated_cases: kpiRollup.automatedCases,
          manual_cases: manualCases,
          automation_rate: toPercent(kpiRollup.automationRate),
          eligible_cases: kpiRollup.eligibleCases
        }
      },
      trend: response.dailyTrend.map(point => ({
        date: point.snapshotDate,
        total_automated: point.automatedCases,
        total_manual: point.totalCases - point.automatedCases,
        automation_rate: toPercent(point.automationRate)
      })),
      volume_by_category: response.volumeByCaseType.map(entry => ({
        category: entry.caseType,
        total: entry.caseCount
      })),
      automation_results: automationResults
    },
    filters: {
      health_system_code: params.health_system_code || null,
      service_line: params.service_line || null,
      dos_start: params.dos_start,
      dos_end: params.dos_end,
      eligible_only: true
    }
  };
};

export const fetchAutomationSummary = (
  params: FetchSummaryParams
): Promise<CMx.AutomationDashboardSummary> =>
  api<AutomationSummaryResponse>({
    endpoint: buildSummaryEndpoint(params),
    init: { method: HttpMethods.GET }
  }).then(response => adaptSummary(response, params));

export const fetchFilterOptions = (): Promise<FilterOptionsResponse> =>
  api<FilterOptionsResponse>({
    endpoint: buildFilterOptionsEndpoint(),
    init: { method: HttpMethods.GET }
  });

export const fetchDatasetFreshness = (
  dataset: string = AUTOMATION_DATASET
): Promise<DatasetFreshnessResponse> =>
  api<DatasetFreshnessResponse>({
    endpoint: buildFreshnessEndpoint(dataset),
    init: { method: HttpMethods.GET }
  });
